/**
 * Búsqueda en vivo por internet: los resultados propios llegan con el POST y los de
 * internet después, por SSE (`live.streamUrl`). Si el stream falla (proxy que corta,
 * navegador sin EventSource, sesión vencida) se cae a sondeo de `live.pollUrl` cada
 * 2 s hasta que el estado sea terminal. Una búsqueda reabierta desde el historial ya
 * terminó casi siempre: ahí se sondea directo, sin abrir un stream.
 */

import { useEffect, useReducer } from "react";
import type { LiveState, LiveStatus, SearchResponse, WebResult } from "../../shared/contracts.js";
import { api, errorMessage } from "../api.js";

export type LiveInit = SearchResponse["live"];

export interface LiveView {
  status: LiveStatus;
  reason: string | null;
  queries: LiveState["queries"];
  results: WebResult[];
  providers: LiveState["providers"];
  error: string | null;
  transport: "sse" | "poll" | null;
  coverage: LiveState["coverage"];
  finishedAt: string | null;
  refreshedAt: string | null;
}

const TERMINAL: ReadonlySet<LiveStatus> = new Set(["done", "partial", "error", "disabled"]);
const POLL_MS = 2000;
/** Tope de espera: el servidor corta la búsqueda en vivo mucho antes (presupuesto de 8 s + cola). */
const MAX_WAIT_MS = 120_000;

type Action =
  | { type: "reset"; init: LiveInit | null }
  | { type: "transport"; transport: "sse" | "poll" }
  | { type: "status"; status: LiveStatus; queries?: LiveState["queries"]; coverage?: LiveState["coverage"] }
  | { type: "result"; result: WebResult }
  | { type: "provider"; stat: LiveState["providers"][number] }
  | { type: "done"; status: LiveStatus }
  | { type: "snapshot"; state: LiveState }
  | { type: "fail"; message: string };

function initial(init: LiveInit | null): LiveView {
  return {
    status: init?.status ?? "disabled",
    reason: init?.reason ?? null,
    queries: [],
    results: [],
    providers: [],
    error: null,
    transport: null,
    coverage: null,
    finishedAt: null,
    refreshedAt: null,
  };
}

export function liveReducer(s: LiveView, a: Action): LiveView {
  switch (a.type) {
    case "reset":
      return initial(a.init);
    case "transport":
      return { ...s, transport: a.transport };
    case "status":
      return { ...s, status: a.status, queries: a.queries ?? s.queries, coverage: a.coverage ?? s.coverage };
    case "result":
      return s.results.some((r) => r.id === a.result.id) ? s : { ...s, results: [...s.results, a.result] };
    case "provider": {
      const rest = s.providers.filter((p) => !(p.provider === a.stat.provider && p.queryId === a.stat.queryId));
      return { ...s, providers: [...rest, a.stat] };
    }
    case "done":
      return { ...s, status: a.status };
    case "snapshot":
      return {
        ...s,
        status: a.state.status,
        queries: a.state.queries,
        results: a.state.results,
        providers: a.state.providers,
        error: a.state.error,
        coverage: a.state.coverage ?? null,
        finishedAt: a.state.finishedAt ?? null,
        refreshedAt: a.state.refreshedAt ?? null,
      };
    case "fail":
      return { ...s, status: "error", error: a.message };
  }
}

function parse<T>(data: string): T | null {
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}

/** restartKey: al cambiar, se vuelve a seguir la corrida desde cero (tras «Actualizar»). */
export function useLiveSearch(searchId: string | null, init: LiveInit | null, mode: "stream" | "poll", refreshToken = 0, restartKey = 0): LiveView {
  const [state, dispatch] = useReducer(liveReducer, init, initial);
  const terminal = TERMINAL.has(state.status);

  // El análisis profundo sigue agregando resultados después de que la búsqueda en
  // vivo terminó (expansión y pistas): cada aviso suyo pide una lectura nueva.
  useEffect(() => {
    const url = init?.pollUrl;
    if (!refreshToken || !terminal || !url) return;
    const ac = new AbortController();
    api.searches.live(url, ac.signal).then((st) => dispatch({ type: "snapshot", state: st })).catch(() => {});
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const streamUrl = init?.streamUrl ?? null;
  const pollUrl = init?.pollUrl ?? null;
  const initStatus = init?.status ?? null;
  const initReason = init?.reason;

  useEffect(() => {
    const liveInit: LiveInit | null = initStatus
      ? { status: initStatus, streamUrl, pollUrl, ...(initReason ? { reason: initReason } : {}) }
      : null;
    dispatch({ type: "reset", init: liveInit });
    if (!searchId || !initStatus || initStatus === "disabled" || (!streamUrl && !pollUrl)) return;

    let cancelled = false;
    let es: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const ac = new AbortController();
    const startedAt = Date.now();

    const schedule = () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        dispatch({ type: "fail", message: "La búsqueda en internet no respondió a tiempo." });
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    async function poll(): Promise<void> {
      if (cancelled || !pollUrl) return;
      dispatch({ type: "transport", transport: "poll" });
      try {
        const st = await api.searches.live(pollUrl, ac.signal);
        if (cancelled) return;
        failures = 0;
        dispatch({ type: "snapshot", state: st });
        if (!TERMINAL.has(st.status)) schedule();
      } catch (err) {
        if (cancelled || ac.signal.aborted) return;
        failures += 1;
        if (failures >= 3) dispatch({ type: "fail", message: errorMessage(err) });
        else schedule();
      }
    }

    function stream(url: string): void {
      dispatch({ type: "transport", transport: "sse" });
      const source = new EventSource(url);
      es = source;
      let finished = false;
      const seen = new Set<string>();
      source.addEventListener("status", (ev) => {
        const d = parse<{ status: LiveStatus; queries?: LiveState["queries"]; coverage?: LiveState["coverage"] }>((ev as MessageEvent<string>).data);
        if (d) dispatch({ type: "status", status: d.status, queries: d.queries, coverage: d.coverage });
      });
      source.addEventListener("result", (ev) => {
        const d = parse<WebResult>((ev as MessageEvent<string>).data);
        if (d) {
          seen.add(d.id);
          dispatch({ type: "result", result: d });
        }
      });
      source.addEventListener("provider", (ev) => {
        const d = parse<LiveState["providers"][number]>((ev as MessageEvent<string>).data);
        if (d) dispatch({ type: "provider", stat: d });
      });
      source.addEventListener("done", (ev) => {
        finished = true;
        source.close();
        const d = parse<{ status: LiveStatus; count: number }>((ev as MessageEvent<string>).data);
        dispatch({ type: "done", status: d?.status ?? "done" });
        // Si se perdió algún evento (reconexión), una lectura final deja el panel completo.
        if (d && d.count !== seen.size && pollUrl) void poll();
      });
      source.onerror = () => {
        if (finished || cancelled) return;
        // EventSource reintenta solo; preferimos el sondeo, que sí informa 401/404.
        source.close();
        es = null;
        if (pollUrl) void poll();
        else dispatch({ type: "fail", message: "Se perdió la conexión con la búsqueda en internet." });
      };
    }

    if (mode === "stream" && streamUrl && typeof EventSource !== "undefined") stream(streamUrl);
    else void poll();

    return () => {
      cancelled = true;
      ac.abort();
      es?.close();
      if (timer) clearTimeout(timer);
    };
  }, [searchId, streamUrl, pollUrl, initStatus, initReason, mode, restartKey]);

  return state;
}

/** Resultado cuya mención viene de OTRA publicación (ver borrowedMentions). */
export interface BorrowedMention {
  /** Publicación de origen (consulta SRC del servidor), si se encontró. */
  sourceUrl: string | null;
}

const BORROWING_DOMAINS = /(^|\.)(instagram\.com|facebook\.com|threads\.(net|com))$/;

/** Frases del extracto con algún dígito (donde va una patente, un teléfono o un RUT). */
function snippetSentences(snippet: string | null): string[] {
  return (snippet ?? "")
    .replace(/^\s*(\.\.\.|…)\s*/, "")
    .split(/(?<=[.!?])\s+/)
    .map((x) => x.trim())
    .filter((x) => x.length >= 12 && /\d/.test(x));
}

/** Plegado sin puntuación, para buscar una leyenda dentro de un extracto. */
function loose(s: string): string {
  return s.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** ¿Es el post de origen que encontró el servidor (consulta SRC)? */
export function isSourcePost(r: Pick<WebResult, "queryId">): boolean {
  return /^SRC(#|$)/.test(r.queryId);
}

/**
 * Menciones "prestadas" en Instagram/Facebook. Junto a un post, Instagram muestra
 * otros posts del perfil y sugeridos, y Google copia en el extracto la descripción
 * automática de ESAS fotos (Instagram lee el texto de la imagen: "SW·KY·89 CHILE
 * Galaxy A52"). Así varios posts que no tienen la patente parecen mencionarla, y el
 * que sí la tiene ni aparece. Son prestadas:
 *   - las que comparten la misma frase con 2+ resultados (ninguno es el origen:
 *     el original no lo devuelve Google al buscar la patente);
 *   - las que traen en el extracto la leyenda del post de origen que encontró el
 *     servidor (consulta SRC, ver live-search/borrowed.ts).
 * Devuelve las prestadas (por id) con la URL del post de origen, si se encontró.
 */
export function borrowedMentions(results: readonly WebResult[]): Map<string, BorrowedMention> {
  const social = results.filter((r) => BORROWING_DOMAINS.test(r.domain.replace(/^www\./, "")) && !isSourcePost(r));
  const sources = results.filter(isSourcePost);
  const out = new Map<string, BorrowedMention>();
  const sourceFor = (r: WebResult): string | null =>
    sources.find((src) => src.entity === r.entity && loose(r.snippet ?? "").includes(loose(src.title)))?.url
    ?? sources.find((src) => src.entity === r.entity)?.url ?? null;

  const byFragment = new Map<string, Set<WebResult>>();
  for (const r of social) {
    for (const frag of snippetSentences(r.snippet)) byFragment.set(frag, (byFragment.get(frag) ?? new Set()).add(r));
    // Trae la leyenda de un post de origen encontrado: la mención es de ese post.
    if (sources.some((src) => src.entity === r.entity && loose(src.title).length >= 8 && loose(r.snippet ?? "").includes(loose(src.title)))) {
      out.set(r.id, { sourceUrl: sourceFor(r) });
    }
  }
  for (const group of byFragment.values()) {
    if (group.size < 2) continue;
    for (const r of group) if (!out.has(r.id)) out.set(r.id, { sourceUrl: sourceFor(r) });
  }
  return out;
}

/** Pista para una búsqueda nueva desde un resultado ("Seguir la pista"). */
export interface PivotRequest {
  /** Texto de la búsqueda nueva en el historial. */
  label: string;
  pivot: { kind: "phrase"; phrase: string } | { kind: "within"; site: string; terms?: string };
}

/** Nombre del aviso desde su título: el primer trozo con 3+ palabras entre emojis ("🚗 CHERY TIGGO 3 … 🚗"). */
export function adPhrase(title: string): string | null {
  const clean = (s: string): string => s.replace(/[^\p{L}\p{M}\p{N}' .-]/gu, " ").replace(/\s+/g, " ").trim();
  const part = title.replace(/(\.\.\.|…).*$/u, "").split(/[\p{Extended_Pictographic}|·•–—]+/u)
    .map(clean).find((x) => x.split(" ").length >= 3);
  const p = (part ?? "").split(" ").slice(0, 12).join(" ");
  return p.length >= 12 ? p : null;
}

/** Quién publicó un resultado de red social, cuando se puede saber con seguridad. */
export interface Publisher {
  /** "@usuario", nombre de la cuenta o "Grupo de Facebook". */
  label: string;
  /** Perfil, página o grupo (para abrirlo y buscar más), o null. */
  url: string | null;
  kind: "cuenta" | "grupo" | "pagina";
}

const NOT_USER = new Set(["p", "reel", "reels", "tv", "stories", "explore", "popular", "accounts", "discover", "tag", "search", "music", "video", "watch"]);
const FB_NOT_PAGE = new Set(["groups", "marketplace", "permalink.php", "story.php", "photo.php", "photo", "watch", "reel", "share", "events", "hashtag", "public", "profile.php", "login", "pages", "people", "search"]);

/**
 * Autor de un resultado de red social: solo de la URL del post y de los textos que
 * Google toma de ESA página (título, "Photo by X on <fecha>", "(@usuario)"). Nunca de
 * los textos de publicaciones vecinas que Facebook/Instagram muestran al lado (los
 * "Nombre ▻ Grupo" de otros posts), que darían un autor equivocado.
 */
export function publisherOf(r: Pick<WebResult, "url" | "title" | "snippet" | "domain">): Publisher | null {
  let u: URL;
  try { u = new URL(r.url); } catch { return null; }
  const host = u.hostname.replace(/^(www|m|web|cl|es)\./, "");
  const seg = u.pathname.split("/").filter(Boolean);
  const title = r.title ?? "";
  const snippet = r.snippet ?? "";
  const handleIn = (s: string): string | null => /\(@([A-Za-z0-9._]{2,30})\)/.exec(s)?.[1] ?? null;

  if (host === "instagram.com") {
    const user = seg[0] && !NOT_USER.has(seg[0].toLowerCase()) ? seg[0] : handleIn(title);
    if (user) return { label: `@${user}`, url: `https://www.instagram.com/${user}/`, kind: "cuenta" };
    // "… - usuario on October 9, 2026:" (descripción de la propia página).
    const meta = /\s[-–]\s([A-Za-z0-9._]{2,30}) on [A-Z][a-z]+ \d{1,2}, \d{4}/.exec(snippet)?.[1];
    if (meta) return { label: `@${meta}`, url: `https://www.instagram.com/${meta}/`, kind: "cuenta" };
    const by = /Photo by (.{2,60}?) on [A-Z][a-z]+ \d{1,2}, \d{4}/.exec(snippet)?.[1];
    if (by) return { label: by.trim(), url: null, kind: "cuenta" };
    return null;
  }
  if (host === "tiktok.com") {
    const user = seg[0]?.startsWith("@") ? seg[0].slice(1) : handleIn(title);
    return user ? { label: `@${user}`, url: `https://www.tiktok.com/@${user}`, kind: "cuenta" } : null;
  }
  if (host === "x.com" || host === "twitter.com") {
    const user = seg[0] && !["i", "search", "hashtag", "home"].includes(seg[0]) ? seg[0] : null;
    return user ? { label: `@${user}`, url: `https://x.com/${user}`, kind: "cuenta" } : null;
  }
  if (host === "youtube.com") {
    const user = seg[0]?.startsWith("@") ? seg[0].slice(1) : /(?:^|\s|\.)@([A-Za-z0-9._-]{3,40}?)(?=No likes|\d[\d.,]*[KkMm]? (?:likes|views)|\s|\.|$)/.exec(snippet)?.[1];
    return user ? { label: `@${user}`, url: `https://www.youtube.com/@${user}`, kind: "cuenta" } : null;
  }
  if (host === "facebook.com") {
    if (seg[0] === "groups" && seg[1]) {
      return { label: /^\d+$/.test(seg[1]) ? "Grupo de Facebook" : `Grupo ${seg[1]}`, url: `https://www.facebook.com/groups/${seg[1]}/`, kind: "grupo" };
    }
    if (seg[0] && !FB_NOT_PAGE.has(seg[0].toLowerCase())) {
      return { label: seg[0], url: `https://www.facebook.com/${seg[0]}/`, kind: "pagina" };
    }
    return null;
  }
  if (host.endsWith("linkedin.com") && seg[0] === "in" && seg[1]) {
    return { label: title.split(/\s[-–|]\s/)[0]!.trim() || seg[1], url: `https://www.linkedin.com/in/${seg[1]}/`, kind: "cuenta" };
  }
  return null;
}

/** Minúsculas sin tildes, para comparar lugares. */
function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase();
}

/** ¿El resultado menciona alguno de los lugares (comuna o región) en título, extracto o dominio? */
export function mentionsArea(r: Pick<WebResult, "title" | "snippet" | "domain">, area: readonly string[]): boolean {
  if (area.length === 0) return false;
  const text = fold(`${r.title} ${r.snippet ?? ""} ${r.domain}`);
  return area.some((a) => {
    const f = fold(a).trim();
    return f.length >= 3 && new RegExp(`(^|[^a-z0-9])${f.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(text);
  });
}

/** Palabras que no sirven para ordenar por palabras clave (las mismas que ignora la búsqueda en vivo). */
const KEYWORD_STOPWORDS = new Set([
  "con", "sin", "del", "los", "las", "una", "uno", "unos", "unas", "por", "para", "que", "como", "mas", "muy", "pero",
  "este", "esta", "esto", "estos", "estas", "ese", "esa", "eso", "esos", "esas", "sus", "les", "nos", "fue", "son", "hay",
  "han", "era", "ser", "entre", "desde", "hasta", "sobre", "tras", "segun", "donde", "cuando", "cual", "quien", "todo",
  "toda", "todos", "todas", "otro", "otra", "otros", "otras", "the", "and", "for",
]);

/** Palabras clave del caso (texto libre de la búsqueda), plegadas: solo letras (3+), sin palabras vacías. */
export function liveKeywords(freeText: readonly string[]): string[] {
  const out = new Set<string>();
  for (const t of freeText) for (const w of fold(t).split(/[^a-z]+/)) if (w.length >= 3 && !KEYWORD_STOPWORDS.has(w)) out.add(w);
  return [...out];
}

/** ¿El resultado trae alguna palabra clave del caso en título o extracto? */
export function mentionsKeyword(r: Pick<WebResult, "title" | "snippet">, keywords: readonly string[]): boolean {
  if (keywords.length === 0) return false;
  const words = new Set(fold(`${r.title} ${r.snippet ?? ""}`).split(/[^a-z0-9]+/));
  return keywords.some((k) => words.has(k));
}

/**
 * Agrupa por entidad y ordena: coincidencias exactas primero, luego los que traen
 * más señales del caso (una palabra clave, la zona) y luego por posición.
 */
export function groupLiveResults(
  results: WebResult[], area: readonly string[] = [], keywords: readonly string[] = [],
): { entity: WebResult["entity"]; items: WebResult[] }[] {
  // Todos los tipos: antes solo persona/patente/vehículo y lo demás se buscaba pero no se mostraba.
  const order: WebResult["entity"][] = ["phrase", "within", "person", "plate", "vehicle", "phone", "rut", "email", "company", "vin", "text"];
  const signals = (r: WebResult): number => Number(mentionsKeyword(r, keywords)) + Number(mentionsArea(r, area));
  return order
    .map((entity) => ({
      entity,
      items: results
        .filter((r) => r.entity === entity)
        .sort((a, b) => Number(b.exactMatch) - Number(a.exactMatch) || signals(b) - signals(a) || a.rank - b.rank),
    }))
    .filter((g) => g.items.length > 0);
}

/** Qué es una consulta además de su dato: noticias, página siguiente, post de origen, expansión o pista. */
export function queryKind(q: Pick<LiveState["queries"][number], "id" | "vertical">): string | null {
  const page = /\.p(\d+)$/.exec(q.id);
  if (page) return `página ${page[1]}`;
  if (/^SRC/.test(q.id)) return "post de origen";
  if (/^X\d/.test(q.id)) return "expansión con IA";
  if (/^Y\d/.test(q.id)) return "pista";
  if (q.vertical === "news") return "noticias";
  return null;
}

/** Resultados que devolvió una consulta (sus llamadas exitosas); null si todavía no corre o falló. */
export function queryResults(providers: LiveState["providers"], id: string): number | null {
  const ok = providers.filter((p) => p.queryId === id && p.ok);
  return ok.length ? ok.reduce((n, p) => n + p.count, 0) : null;
}
